import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, ROLE_INFO } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  LayoutDashboard,
  Building2,
  Users,
  Layers,
  FileSpreadsheet,
  Settings,
  LogOut,
  Menu,
  Sun,
  Moon,
  ChevronDown,
  ChevronRight,
  User,
  BarChart3,
  TrendingUp,
  UserCog,
  ClipboardList,
  Shield,
  CreditCard,
  FileText,
  Webhook,
  UserX,
  BookOpen,
  Banknote,
  Upload,
  Sparkles,
  Scale,
  Gavel,
  Gift,
} from 'lucide-react';
import api from '@/lib/api';

function NavGroup({ label, icon: GroupIcon, items, currentPath, onNavigate, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);

  // Auto-open if current path is in this group
  useEffect(() => {
    const hasActive = items.some(item => item.path === currentPath);
    if (hasActive) setOpen(true);
  }, [currentPath, items]);

  if (items.length === 0) return null;

  return (
    <div className="mb-1">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70 hover:text-muted-foreground transition-colors"
      >
        <ChevronRight className={`h-3 w-3 transition-transform duration-200 ${open ? 'rotate-90' : ''}`} />
        {GroupIcon && <GroupIcon className="h-3.5 w-3.5" />}
        <span>{label}</span>
      </button>
      {open && (
        <div className="space-y-0.5 mt-0.5">
          {items.map((item) => {
            const Icon = item.icon;
            const isActive = currentPath === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={onNavigate}
                className={`flex items-center gap-2.5 pl-8 pr-3 py-2 rounded-md text-[13px] font-medium transition-all ${
                  isActive
                    ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{item.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function DashboardLayout({ children }) {
  const { 
    user, 
    logout, 
    isAdminLevel,
    isLOPManager,
    canManageOrgs, 
    canManageEmployees, 
    canManageVerticals,
    canViewPayroll,
    canEditLOP,
    canManageIncrements,
    canViewReports,
    canViewSettings,
    canManageUsers,
    canViewAudit,
    shouldShowLOPOnly,
  } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unseenCount, setUnseenCount] = useState(0);

  useEffect(() => {
    api.get('/changelog/unseen-count').then(res => setUnseenCount(res.data.count)).catch(() => {});
  }, [location.pathname]);

  // Build grouped navigation
  const peopleItems = [];
  if (canManageEmployees() || shouldShowLOPOnly()) {
    peopleItems.push({ icon: Users, label: 'Employees', path: '/employees' });
  }
  if (canManageEmployees()) {
    peopleItems.push({ icon: FileText, label: 'Employee Docs', path: '/employee-docs' });
  }
  if (canManageVerticals()) {
    peopleItems.push({ icon: Layers, label: 'Verticals', path: '/verticals' });
  }
  if (canManageOrgs() || shouldShowLOPOnly()) {
    peopleItems.push({ icon: Building2, label: 'Organizations', path: '/organizations' });
  }

  const payrollItems = [];
  if (canViewPayroll() || canEditLOP()) {
    payrollItems.push({
      icon: FileSpreadsheet,
      label: shouldShowLOPOnly() ? 'LOP Days' : 'Salary Entry',
      path: '/salary-reports'
    });
    payrollItems.push({
      icon: FileSpreadsheet,
      label: 'Blogic Salary Entry',
      path: '/blogic-salary-entry'
    });
    if (isAdminLevel() || shouldShowLOPOnly()) {
      payrollItems.push({ icon: Upload, label: 'Keka Upload', path: '/keka-upload' });
    }
    payrollItems.push({ icon: UserX, label: 'Resigned Employees', path: '/resigned-employees' });
    payrollItems.push({ icon: Banknote, label: 'Retention Bonus', path: '/retention-bonus' });
    payrollItems.push({ icon: Gift, label: 'Annual Bonus', path: '/annual-bonus' });
    if (isAdminLevel() || isLOPManager()) {
      payrollItems.push({ icon: Gavel, label: 'Penalties', path: '/penalties' });
    }
  }

  const financeItems = [];
  if (canManageIncrements()) {
    financeItems.push({ icon: TrendingUp, label: 'Increments', path: '/increments' });
    financeItems.push({ icon: CreditCard, label: 'Advances', path: '/advances' });
  }

  const reportItems = [];
  if (canViewReports()) {
    reportItems.push({ icon: BarChart3, label: 'Reports', path: '/reports' });
    reportItems.push({ icon: Users, label: 'Employee Salary Report', path: '/employee-salary-report' });
    reportItems.push({ icon: Shield, label: 'EPF Report', path: '/epf-report' });
  }

  const adminItems = [];
  if (canManageUsers()) {
    adminItems.push({ icon: UserCog, label: 'Users', path: '/users' });
  }
  if (canViewAudit()) {
    adminItems.push({ icon: ClipboardList, label: 'Audit Logs', path: '/audit-logs' });
  }
  if (canViewSettings()) {
    adminItems.push({ icon: Settings, label: 'Settings', path: '/settings' });
  }

  const integrationItems = [];
  if (isAdminLevel()) {
    integrationItems.push({ icon: LayoutDashboard, label: 'Zoho Dashboard', path: '/zoho-dashboard' });
    integrationItems.push({ icon: Webhook, label: 'Zoho Integration', path: '/zoho-integration' });
  }

  const legalItems = [];
  if (isAdminLevel() || isLOPManager()) {
    legalItems.push({ icon: FileText, label: 'Salary Slip Generator', path: '/legal/salary-slip' });
  }

  // Flat list for header title lookup
  const allItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    ...peopleItems, ...payrollItems, ...financeItems, ...reportItems,
    ...adminItems, ...integrationItems, ...legalItems,
    { icon: BookOpen, label: 'Docs', path: '/docs' },
    { icon: Sparkles, label: "What's New", path: '/changelog' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleColor = (role) => {
    const info = ROLE_INFO[role];
    if (!info) return 'bg-gray-100 text-gray-700';
    const colors = {
      red: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
      blue: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
      amber: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
      green: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
    };
    return colors[info.color] || colors.gray;
  };

  const getRoleLabel = (role) => {
    return ROLE_INFO[role]?.label || role;
  };

  const closeMobile = () => setSidebarOpen(false);

  const NavContent = () => (
    <div className="flex flex-col h-full">
      <nav className="flex-1 px-3 py-3 overflow-y-auto space-y-0.5">
        {/* Dashboard - standalone */}
        <Link
          to="/dashboard"
          data-testid="nav-dashboard"
          onClick={closeMobile}
          className={`flex items-center gap-2.5 px-3 py-2.5 rounded-md text-[13px] font-medium transition-all ${
            location.pathname === '/dashboard'
              ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
        >
          <LayoutDashboard className="h-4 w-4" />
          Dashboard
        </Link>

        <div className="h-px bg-border my-2" />

        <NavGroup label="People" icon={Users} items={peopleItems} currentPath={location.pathname} onNavigate={closeMobile} />
        <NavGroup label="Payroll" icon={FileSpreadsheet} items={payrollItems} currentPath={location.pathname} onNavigate={closeMobile} />
        {financeItems.length > 0 && <NavGroup label="Finance" icon={CreditCard} items={financeItems} currentPath={location.pathname} onNavigate={closeMobile} />}
        {reportItems.length > 0 && <NavGroup label="Reports" icon={BarChart3} items={reportItems} currentPath={location.pathname} onNavigate={closeMobile} />}
        {adminItems.length > 0 && (
          <>
            <div className="h-px bg-border my-2" />
            <NavGroup label="Admin" icon={Shield} items={adminItems} currentPath={location.pathname} onNavigate={closeMobile} />
          </>
        )}
        {integrationItems.length > 0 && <NavGroup label="Integrations" icon={Webhook} items={integrationItems} currentPath={location.pathname} onNavigate={closeMobile} />}
        {legalItems.length > 0 && <NavGroup label="Legal" icon={Scale} items={legalItems} currentPath={location.pathname} onNavigate={closeMobile} />}

        {/* Docs - standalone at bottom of nav */}
        <div className="h-px bg-border my-2" />
        <Link
          to="/changelog"
          data-testid="nav-whats-new"
          onClick={closeMobile}
          className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] font-medium transition-all ${
            location.pathname === '/changelog'
              ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
        >
          <Sparkles className="h-4 w-4" />
          What's New
          {unseenCount > 0 && (
            <span className="ml-auto flex h-4.5 min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">{unseenCount}</span>
          )}
        </Link>
        <Link
          to="/docs"
          data-testid="nav-docs"
          onClick={closeMobile}
          className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] font-medium transition-all ${
            location.pathname.startsWith('/docs')
              ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
        >
          <BookOpen className="h-4 w-4" />
          Docs
        </Link>
      </nav>

      {/* User card */}
      <div className="p-3 border-t bg-muted/30">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center shrink-0">
            <User className="h-4 w-4 text-slate-600 dark:text-slate-300" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate leading-tight">{user?.name}</p>
            <Badge className={`mt-0.5 text-[10px] px-1.5 py-0 ${getRoleColor(user?.role)}`}>
              {getRoleLabel(user?.role)}
            </Badge>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-60 flex-col border-r bg-card">
        <div className="px-4 py-4 border-b">
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-slate-900 dark:bg-white flex items-center justify-center">
              <FileSpreadsheet className="h-4.5 w-4.5 text-white dark:text-slate-900" />
            </div>
            <span className="text-lg font-bold tracking-tight">PayTrack</span>
          </Link>
        </div>
        <NavContent />
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-14 border-b bg-card flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-3">
            {/* Mobile Menu */}
            <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-60 p-0">
                <div className="px-4 py-4 border-b">
                  <div className="flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-lg bg-slate-900 dark:bg-white flex items-center justify-center">
                      <FileSpreadsheet className="h-4.5 w-4.5 text-white dark:text-slate-900" />
                    </div>
                    <span className="text-lg font-bold tracking-tight">PayTrack</span>
                  </div>
                </div>
                <NavContent />
              </SheetContent>
            </Sheet>

            <h1 className="text-sm font-semibold hidden sm:block text-muted-foreground">
              {allItems.find(item => item.path === location.pathname)?.label || 'PayTrack'}
            </h1>
          </div>

          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle-btn"
              className="rounded-full h-8 w-8"
            >
              {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 h-9 px-2" data-testid="user-menu-btn">
                  <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center">
                    <User className="h-3.5 w-3.5" />
                  </div>
                  <span className="hidden sm:inline text-sm">{user?.name}</span>
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div>
                    <p className="font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="flex items-center gap-2">
                  <span className="text-muted-foreground">Role:</span>
                  <Badge className={getRoleColor(user?.role)} variant="secondary">
                    {getRoleLabel(user?.role)}
                  </Badge>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          <div className="max-w-7xl mx-auto animate-fade-in">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

export default DashboardLayout;
